import { createSlice } from '@reduxjs/toolkit';
// eslint-disable-next-line no-duplicate-imports -- FIXME
import { createSelector } from '@reduxjs/toolkit';
import { updateDiet } from '@store/myDiet/myDiet.actions';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  isAfter,
  startOfMonth,
  subDays,
} from 'date-fns';
import first from 'lodash/first';
import last from 'lodash/last';
import pick from 'lodash/pick';
import { format, formatArray } from '@services/Date.service';

import {
  resetMealtypeTagsReducer,
  resetTagsReducer,
  toggleMealtypeTagReducer,
  toggleTagReducer,
} from '../orderForm/orderForm.actions';

const today = new Date();

const initialState = {
  selectedDietType: 'INIT',
  selectedDate: format(today),
  selectedDietId: 0,
  selectedDiet: {},
  selectedTags: [],
  selectedMealtypeTags: {},
  paymentMethod: null,

  calendarDays: formatArray(
    eachDayOfInterval({
      start: startOfMonth(today),
      end: endOfMonth(today),
    })
  ),
  menuPlanningCalendarDays: [],
  earliestDietDay: format(today),
};

const myDietSlice = createSlice({
  name: 'myDiet',
  initialState,
  reducers: {
    setDiet: (state, { payload }) => {
      const { diet, options = {} } = payload;
      const isTodayAfterFirstDeliveryDate = isAfter(
        today,
        new Date(diet?.firstDeliveryDate)
      );

      const earliestDietDay = isTodayAfterFirstDeliveryDate
        ? today
        : new Date(diet?.firstDeliveryDate);
      const startDate = startOfMonth(earliestDietDay);
      const endDate = endOfMonth(earliestDietDay);
      const rangeDays = formatArray(
        eachDayOfInterval({ start: startDate, end: endDate })
      );

      state.selectedDietId = diet?.id;
      state.selectedDiet = diet;
      state.selectedDietType = 'ORDER_CLICK';
      if (!(options?.skipSetDate ?? false)) {
        state.selectedDate = isTodayAfterFirstDeliveryDate
          ? format(today)
          : format(new Date(diet?.firstDeliveryDate));
      }
      state.calendarDays = rangeDays;
      state.earliestDietDay = format(earliestDietDay);
    },
    setMyDiet: (state, { payload }) => {
      // eslint-disable-next-line array-callback-return -- FIXME
      Object.entries(payload).map(([key, value]) => {
        state[key] = value;
      });
    },
    setSelectedDate: (state, { payload: selectedDate }) => {
      state.selectedDate = selectedDate;
    },
    setCalendarDays: (state, { payload: rangeDays }) => {
      state.calendarDays = rangeDays;
    },
    setMenuPlanningCalendarDays: (state, { payload: rangeDays }) => {
      state.menuPlanningCalendarDays = rangeDays;
    },
    addMenuPlanningCalendarDays: (state, { payload }) => {
      const { direction, visibleDays } = payload;
      const isNext = direction === 'next';
      const day = isNext
        ? last(state.menuPlanningCalendarDays)
        : first(state.menuPlanningCalendarDays);

      const fullDate = new Date(day);
      const startDay = isNext
        ? subDays(fullDate, visibleDays * 2 - 1)
        : subDays(fullDate, visibleDays);
      const endDay = isNext
        ? addDays(fullDate, visibleDays)
        : addDays(fullDate, visibleDays * 2 - 1);

      const rangeDays = formatArray(
        eachDayOfInterval({
          start: startDay,
          end: endDay,
        })
      );

      state.menuPlanningCalendarDays = rangeDays;
    },

    resetTags: resetTagsReducer,
    toggleTag: toggleTagReducer,
    resetMealtypeTags: resetMealtypeTagsReducer,
    toggleMealtypeTag: toggleMealtypeTagReducer,
    resetMyDiet: () => initialState,
  },
  extraReducers: {
    [updateDiet.fulfilled]: (state, { payload }) => {
      state.selectedDiet = { ...state.selectedDiet, ...payload };
    },
  },
});

export const selectMyDiet = state => state.myDiet;
export const selectSelectedDate = ({ myDiet }) => myDiet.selectedDate;
export const selectSelectedDiet = ({ myDiet }) => myDiet.selectedDiet;
export const selectSelectedDietId = ({ myDiet }) => myDiet.selectedDietId;

export const selectMyDietPick = createSelector(
  [selectMyDiet, (_, predicate) => predicate],
  (myDiet, predicate) => {
    if (predicate.length === 1) {
      // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
      return pick(myDiet, predicate)[predicate[0]]; // return only value
    }

    // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
    return pick(myDiet, predicate); // return object
  }
);

export const {
  addMenuPlanningCalendarDays,
  resetTags,
  setSelectedDate,
  setCalendarDays,
  setDiet,
  setMenuPlanningCalendarDays,
  setMyDiet,
  toggleTag,
  resetMyDiet,
  resetMealtypeTags,
  selectedMealtypeTags,
  toggleMealtypeTag,
} = myDietSlice.actions;

export default myDietSlice.reducer;
